import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EventService from '../../../services/EventService';
import ArtistService from '../../../services/ArtistService';

function formatLocalDateTime(dateString) {
    const date = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function EventCreateAndroid() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [artists, setArtists] = useState([]);
    const [form, setForm] = useState({
        name: '',
        venue: '',
        artistId: '',
        eventDate: '',
        capacity: 0,
        vatPercentage: 0,
        description: '',
        acceptedPaymentTypes: [],
        categories: [{ name: '', price: 0, id: '', eventId: '' }],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        const loadArtists = async () => {
            try {
                const res = await ArtistService.getArtistsForAuthenticatedUser();
                setArtists(res.data || []);
            } catch {}
        };
        loadArtists();
    }, []);

    useEffect(() => {
        if (!id) return;
        setEditMode(true);
        const fetchEvent = async () => {
            try {
                const response = await EventService.getEventById(id);
                const ev = response.data;
                setForm({
                    ...ev,
                    artistId: ev.artistId,
                    eventDate: formatLocalDateTime(ev.eventDate),
                    categories: (ev.categories || []).map(cat => ({ name: cat.name, price: cat.price / 100, id: cat.id, eventId: cat.eventId })),
                    acceptedPaymentTypes: ev.acceptedPaymentTypes || [],
                });
            } catch (e) {
                setError('Error loading event data.');
            }
        };
        fetchEvent();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const togglePaymentType = (type) => {
        setForm(prev => {
            const exists = prev.acceptedPaymentTypes.includes(type);
            return {
                ...prev,
                acceptedPaymentTypes: exists
                    ? prev.acceptedPaymentTypes.filter(t => t !== type)
                    : [...prev.acceptedPaymentTypes, type]
            };
        });
    };

    const handleCategoryChange = (index, field, value) => {
        const updated = form.categories.map((c, i) => i === index ? { ...c, [field]: value } : c);
        setForm(prev => ({ ...prev, categories: updated }));
    };

    const addCategory = () => setForm(prev => ({ ...prev, categories: [...prev.categories, { name: '', price: 0 }] }));
    const removeCategory = (index) => setForm(prev => ({ ...prev, categories: prev.categories.filter((_, i) => i !== index) }));

    const save = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        const payload = {
            ...form,
            categories: form.categories.map(c => ({ ...c, price: Math.round((Number(c.price) || 0) * 100) })),
            artistId: form.artistId,
        };
        try {
            if (editMode) {
                await EventService.updateEvent(payload);
            } else {
                await EventService.createEvent(payload);
            }
            navigate('/events');
        } catch (err) {
            setError(err.response?.data || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="px-4 py-4">
            <h1 className="text-2xl font-bold mb-3">{editMode ? 'Edit Event' : 'Create Event'}</h1>
            {error ? <div className="mb-3 text-red-600">{error}</div> : null}
            <form onSubmit={save} className="space-y-4">
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Event Name</label>
                    <input name="name" value={form.name} onChange={handleChange} className="w-full border rounded-md px-4 py-3" required />
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Venue</label>
                    <input name="venue" value={form.venue} onChange={handleChange} className="w-full border rounded-md px-4 py-3" required />
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Artist</label>
                    <select name="artistId" value={form.artistId || ''} onChange={handleChange} className="w-full border rounded-md px-4 py-3" required>
                        <option value="" disabled>Select artist</option>
                        {artists.map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Event Date</label>
                    <input type="datetime-local" name="eventDate" value={form.eventDate} onChange={handleChange} className="w-full border rounded-md px-4 py-3" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">Capacity</label>
                        <input type="number" name="capacity" value={form.capacity} onChange={handleChange} className="w-full border rounded-md px-4 py-3" min="0" required />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">VAT %</label>
                        <input type="number" name="vatPercentage" value={form.vatPercentage} onChange={handleChange} className="w-full border rounded-md px-4 py-3" min="0" required />
                    </div>
                </div>

                <div>
                    <label className="block text-sm text-gray-700 mb-2">Accepted Payment Types</label>
                    <div className="flex gap-3">
                        {['ONSITE', 'CARD'].map(type => (
                            <label key={type} className="flex items-center gap-2">
                                <input type="checkbox" checked={form.acceptedPaymentTypes.includes(type)} onChange={() => togglePaymentType(type)} />
                                <span>{type}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm text-gray-700 mb-2">Ticket Categories</label>
                    {form.categories.map((c, i) => (
                        <div key={i} className="grid grid-cols-5 gap-2 mb-2">
                            <input className="col-span-3 border rounded-md px-3 py-2" placeholder="Name" value={c.name} onChange={(e) => handleCategoryChange(i, 'name', e.target.value)} />
                            <input className="col-span-1 border rounded-md px-3 py-2" placeholder="Price (€)" type="number" min="0" value={c.price} onChange={(e) => handleCategoryChange(i, 'price', e.target.value)} />
                            <button type="button" className="col-span-1 text-red-600" onClick={() => removeCategory(i)}>Remove</button>
                        </div>
                    ))}
                    <button type="button" className="text-blue-600" onClick={addCategory}>Add Category</button>
                </div>

                <div>
                    <label className="block text-sm text-gray-700 mb-1">Description</label>
                    <textarea name="description" value={form.description} onChange={handleChange} className="w-full border rounded-md px-4 py-3" rows="4" />
                </div>

                <button type="submit" className={`w-full bg-blue-600 text-white rounded-md py-3 ${loading ? 'opacity-50' : ''}`} disabled={loading}>
                    {loading ? 'Saving...' : (editMode ? 'Update Event' : 'Create Event')}
                </button>
            </form>
        </div>
    );
}

export default EventCreateAndroid;


